import { Attachment } from './attachment.model';
import { attachmentFormat, fileExtension, sanitizeFilename, templateNameFromDescription } from './attachment-display-name';

/**
 * A stored attachment's size, for display. Kilobytes to low megabytes covers the
 * overwhelming majority of case documents, so units stop at MB. One decimal place for
 * MB and none below it: "1.4 MB" is useful, "1434.2 KB" is not, and neither is "1.4392 MB".
 */
function formatBytes(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) {
    return '';
  }
  if (bytes < 1024) {
    return `${Math.round(bytes)} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Short uppercase label for a format key (`docx` -> `DOCX`). */
function formatLabel(format: string): string {
  return format.toUpperCase();
}

/** Best-effort format label when there's no `formats`/`original_format` map to read (legacy rows). */
function fallbackFormatLabel(attachment: Attachment): string {
  if (attachment.is_pdf) {
    return 'PDF';
  }
  if (attachment.is_docx) {
    return 'DOCX';
  }
  const subtype = attachment.mime_type?.split('/')?.[1];
  return subtype ? subtype.split('.').pop().toUpperCase() : '';
}

/** Canonical UUID, optionally with the ` <n>` multi-tenant copy suffix — a placeholder name, not a real one. */
const BARE_UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}( \d+)?$/;

function isBareUuid(name: string | undefined, attachmentId: string | undefined): boolean {
  const trimmed = name?.trim() ?? '';
  if (!trimmed) {
    return true;
  }
  if (attachmentId && trimmed === attachmentId) {
    return true;
  }
  return BARE_UUID.test(trimmed);
}

/**
 * The actual filename this attachment was generated or uploaded as. Generated docs store
 * `generated_filename` without an extension, so `original_format` is appended; uploads keep
 * `original_filename` as given (already has one). Empty when only a placeholder UUID is on file.
 *
 * An upload carries a pattern-generated name too (EE-349), stored extensionless like a generated
 * document's, so it is the display name for both; `original_filename` is the record of what was
 * uploaded and the fallback for anything stored before uploads had a generated name.
 */
export function attachmentFilename(attachment: Attachment): string {
  // original_format is optional — anything stored before it was recorded has only its uploaded
  // filename. A legacy upload that is then RENAMED gets an extensionless generated_filename, so
  // without this fallback the list shows "Signed Lease" while the download restores ".pdf" from the
  // signed URL: the display/download mismatch this feature exists to remove.
  //
  // The format comes from the UPLOADED name only, never from the name being formatted: a legacy
  // generated document is stored as "MAQ-182.docx", and reading its extension back off itself would
  // produce "MAQ-182.docx.docx". The equality guard covers the same shape from the other direction.
  const format = attachmentFormat(attachment);
  const withFormat = (name: string) =>
    format && fileExtension(name) !== format ? `${name}.${format}` : name;

  const generated = attachment.generated_filename?.trim();
  if (!isBareUuid(generated, attachment.id)) {
    return withFormat(generated ?? '');
  }
  if (!attachment.generated) {
    const original = attachment.original_filename?.trim();
    if (!isBareUuid(original, attachment.id)) {
      return original ?? '';
    }
  }

  // A generated document whose stored name is a UUID — every template left on the
  // `#unique_identifier#` default — still knows which template produced it, via its description.
  // The download already recovers a readable "<case id> - <template name>" here
  // (AttachmentDownloadNaming.friendlyName); without the same fallback this line showed nothing at
  // all, so the list and the downloaded file disagreed about what the document is called.
  // Sanitize the JOINED result, not just the parts: each half is capped at 120 characters, so
  // concatenating two sanitized halves can produce a 243-character base that blows the cap the
  // backend enforces on the same name. Composing first and sanitizing once keeps the two sides
  // agreeing on what a long name truncates to.
  const prefix = sanitizeFilename(attachment.case_id);
  const templateName = sanitizeFilename(templateNameFromDescription(attachment.description));
  if (templateName) {
    return withFormat(sanitizeFilename(prefix ? `${prefix} - ${templateName}` : templateName) ?? templateName);
  }
  const typeName = sanitizeFilename(attachment.type?.replace(/_/g, ' '));
  if (typeName) {
    return withFormat(sanitizeFilename(prefix ? `${prefix} - ${typeName}` : typeName) ?? typeName);
  }

  // The server's last resort is the attachment id, because a download must have *some* filename.
  // Here it would only put a UUID back on screen — the thing isBareUuid exists to keep out — so
  // this line stays empty and the row shows just the format/size summary.
  return '';
}

/**
 * Rendition(s) summary — format and size. A generated document that has both its original
 * (e.g. DOCX) and a converted rendition (PDF, the one e-filing embeds) shows both, original
 * first: "DOCX 16.5 KB → PDF 42.6 KB". A single-rendition file (a native PDF upload, or a
 * legacy row with no `formats` map) shows just the one. Empty when no size is known at all.
 */
function attachmentRenditionsLine(attachment: Attachment): string {
  const sizes = attachment.sizes;
  const formats = attachment.formats;

  if (sizes && formats && Object.keys(sizes).length > 0) {
    const present = Object.keys(formats).filter((f) => formats[f] && typeof sizes[f] === 'number');
    if (present.length > 0) {
      // Original rendition first (matches how it was actually produced), then whatever
      // else exists, in a stable order so re-renders don't reshuffle the line.
      const ordered = [
        ...present.filter((f) => f === attachment.original_format),
        ...present.filter((f) => f !== attachment.original_format).sort(),
      ];
      return ordered
        .map((f) => {
          const size = formatBytes(sizes[f]);
          return size ? `${formatLabel(f)} · ${size}` : formatLabel(f);
        })
        .join(' → ');
    }
  }

  // Fall back to the scalar `size` field (legacy rows predating the `formats`/`sizes` maps).
  const scalarSize = formatBytes(typeof attachment.size === 'string' ? parseFloat(attachment.size) : attachment.size);
  const label = fallbackFormatLabel(attachment);
  if (!label && !scalarSize) {
    return '';
  }
  return [label, scalarSize].filter(Boolean).join(' · ');
}

/**
 * One line describing an attachment: its actual filename (with extension) when one is on
 * file, followed by the rendition/size summary. Either half can be empty — the two are
 * joined with the same "·" divider used throughout this row.
 */
export function attachmentFileInfoLine(attachment: Attachment): string {
  const filename = attachmentFilename(attachment);
  const renditions = attachmentRenditionsLine(attachment);
  return [filename, renditions].filter(Boolean).join(' · ');
}
